/**
 * ExpertVoice builder integration — presentation only.
 *
 * ExpertVoice owns member pricing end to end: its app embed paints the native
 * Price Display into the builder mount, and its Deal Manager discount applies
 * at checkout for a verified redirect session. The theme never derives a
 * percent and never touches cart money — a builder total is a sum of several
 * variants, and only ExpertVoice knows which of them a given deal covers.
 *
 * This module answers one question: is ExpertVoice showing a member discount on
 * this page? Builders use the answer to show "expert pricing applies at
 * checkout" next to the list-price total, so the pay row never contradicts the
 * checkout total.
 *
 * How the app signals a member (from expertvoice-activate-discount.js and
 * expertvoice-toggle-price-block.js): a shopper arrives with ?ev_referral=, the
 * app writes EV.shopifyDealId to cart attributes, then clones #expertvoice-block
 * beside the configured selector and shows one of four states.
 *
 * Builders read the cart attribute rather than that widget, because ExpertVoice
 * catalogs the products a build is *made of*, not the landing product a builder
 * page is built around. The widget therefore has no verdict to give here, while
 * the components it does cover get discounted in the cart. The widget state is
 * still honoured when present, for pages where it means something.
 */
(function (root) {
  'use strict';

  var CHANGE_EVENT = 'wheelbuilder:expertvoice-changed';
  /* The app clones #expertvoice-block and inserts it *after* the mount, so watch
     the host wrapper, not the mount itself. */
  var HOST_SELECTOR = '[data-wb-ev-native-host], .wb-ev-native-host';
  var BLOCK_SELECTOR = '#expertvoice-block-clone, #expertvoice-block, .expertvoice-block';
  /* The block renders four states (active discount / on sale / ineligible /
     fallback). Only the first one means a member discount is actually coming. */
  var ACTIVE_SELECTOR = '.expertvoice-active-discount-details';
  var PRICE_SELECTOR = '.expertvoice-price';
  /* EV resolves the deal from cart attributes with retries, so the block can
     paint seconds after load; the observer catches it and these are a net. */
  var RECHECK_MS = [0, 400, 1600, 4000, 8000];
  /* Written by the app on an ev_referral landing; present for the cart's life. */
  var DEAL_ATTRIBUTE = 'EV.shopifyDealId';

  var active = false;
  var scheduled = false;
  var observers = [];
  var sessionDeal = false;
  var cartCheckInFlight = false;

  function cfg() {
    return (root && root.__WB_EXPERTVOICE__) || {};
  }

  function enabled() {
    return cfg().enabled !== false;
  }

  function hostSelector() {
    var extra = String(cfg().extraWidgetSelector || '').trim();
    return extra ? HOST_SELECTOR + ', ' + extra : HOST_SELECTOR;
  }

  function queryHosts() {
    if (typeof document === 'undefined' || !document.querySelectorAll) return [];
    try {
      return document.querySelectorAll(hostSelector());
    } catch (_) {
      return document.querySelectorAll(HOST_SELECTOR);
    }
  }

  /* EV toggles its states with inline display, so inline style is the signal. */
  function inlineHidden(el) {
    return !!(el && el.style && el.style.display === 'none');
  }

  function hasPaintedPrice(el) {
    if (!el || !el.querySelector) return false;
    var price = el.querySelector(PRICE_SELECTOR);
    return !!(price && String(price.textContent || '').trim());
  }

  /** True once ExpertVoice shows a real member discount in a builder mount. */
  function hasActiveDeal() {
    var hosts = queryHosts();
    for (var i = 0; i < hosts.length; i++) {
      if (!hosts[i].querySelectorAll) continue;
      var blocks = hosts[i].querySelectorAll(BLOCK_SELECTOR);
      for (var j = 0; j < blocks.length; j++) {
        if (inlineHidden(blocks[j])) continue;
        var details = blocks[j].querySelector(ACTIVE_SELECTOR);
        if (details && !inlineHidden(details) && hasPaintedPrice(details)) return true;
      }
    }
    return false;
  }

  /**
   * Read the member session from cart attributes. This is the signal that works
   * on builder pages: the deal is on the cart, not on the landing product.
   */
  function checkCartDeal() {
    /* Nothing can show the notice when the setting is off, so don't spend a
       request on every builder page view to find that out. */
    if (!enabled()) return;
    if (cartCheckInFlight || typeof fetch !== 'function') return;
    cartCheckInFlight = true;
    var url = '/cart.js';
    try {
      if (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) {
        url = root.Shopify.routes.root + 'cart.js';
      }
    } catch (_) {}

    fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        return res && res.ok ? res.json() : null;
      })
      .then(function (cart) {
        var attrs = (cart && cart.attributes) || {};
        sessionDeal = !!String(attrs[DEAL_ATTRIBUTE] || '').trim();
      })
      .catch(function () {
        /* Leave sessionDeal as-is; the widget signal still applies. */
      })
      .then(function () {
        cartCheckInFlight = false;
        evaluate();
      });
  }

  /* Preview is for styling the notice without an ExpertVoice deal. Allowed in
     the theme editor and on unpublished/development themes, never on the live
     theme — a shopper must not see expert-pricing copy they can't get. */
  function previewAllowed() {
    if (!root.Shopify) return false;
    if (root.Shopify.designMode === true) return true;
    var role = root.Shopify.theme && root.Shopify.theme.role;
    return typeof role === 'string' && role !== '' && role !== 'main';
  }

  function previewRequested() {
    if (typeof location === 'undefined' || !location.search) return false;
    try {
      return new URLSearchParams(location.search).get('wb_ev_preview') === '1';
    } catch (_) {
      return false;
    }
  }

  function noticePreview() {
    return previewRequested() && previewAllowed();
  }

  function evaluate() {
    var next = enabled() && (sessionDeal || hasActiveDeal() || noticePreview());
    if (next === active) return active;
    active = next;
    if (typeof document !== 'undefined' && typeof CustomEvent === 'function') {
      document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { active: active } }));
    }
    return active;
  }

  function scheduleEvaluate() {
    if (scheduled) return;
    scheduled = true;
    var run = function () {
      scheduled = false;
      evaluate();
    };
    if (typeof root.requestAnimationFrame === 'function') {
      root.requestAnimationFrame(run);
    } else {
      setTimeout(run, 16);
    }
  }

  /* Scoped to the mount hosts — a document-wide observer would fire on every
     Alpine paint in the builder. Attributes matter because EV flips its states
     with inline display rather than by re-rendering. */
  function watchHosts() {
    if (typeof MutationObserver !== 'function') return;
    var nodes = queryHosts();
    for (var i = 0; i < observers.length; i++) observers[i].disconnect();
    observers = [];
    for (var j = 0; j < nodes.length; j++) {
      var observer = new MutationObserver(scheduleEvaluate);
      observer.observe(nodes[j], {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
      observers.push(observer);
    }
  }

  function boot() {
    watchHosts();
    checkCartDeal();
    for (var i = 0; i < RECHECK_MS.length; i++) {
      setTimeout(function () {
        watchHosts();
        evaluate();
      }, RECHECK_MS[i]);
    }
    if (typeof document !== 'undefined' && document.addEventListener) {
      document.addEventListener('DOMContentLoaded', scheduleEvaluate);
    }
    if (root.addEventListener) {
      root.addEventListener('load', scheduleEvaluate);
      /* Fired on window by expertvoice-activate-discount.js once the ev_referral
         deal lands on the cart — re-read the attribute it just wrote. */
      root.addEventListener('expertvoice:cart-activated', function (event) {
        var attrs = (event && event.detail) || null;
        if (attrs && String(attrs[DEAL_ATTRIBUTE] || '').trim()) {
          sessionDeal = true;
          evaluate();
          return;
        }
        checkCartDeal();
      });
    }
  }

  function isExpertVoiceActive() {
    return active;
  }

  function refresh() {
    watchHosts();
    checkCartDeal();
    return evaluate();
  }

  /** State for builder notices. ExpertVoice supplies the numbers, we supply words. */
  function expertVoiceNotice() {
    return {
      active: active,
      label: String(cfg().label || 'ExpertVoice'),
    };
  }

  boot();

  var api = {
    isExpertVoiceActive: isExpertVoiceActive,
    expertVoiceNotice: expertVoiceNotice,
    refresh: refresh,
    CHANGE_EVENT: CHANGE_EVENT,
  };

  root.wbIsExpertVoiceActive = isExpertVoiceActive;
  root.wbExpertVoiceNotice = expertVoiceNotice;
  root.wbRefreshExpertVoice = refresh;
  root.__WB_EXPERTVOICE_API__ = api;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis);
